import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";

const { Text, Title } = Typography;

/**
 * Panneau d'édition inline d'un type de paramètre (CAROSSERIE ou GENRE),
 * filtré sur la catégorie sélectionnée. Les valeurs « globales » (sans
 * catégorie) héritées par toutes les catégories sont affichées en lecture
 * seule, pour information.
 */
const ParamPanel = ({ type, titre, categorie }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const categorieId = categorie?.categorieId;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/parametre/type/${type}`);
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
      message.error(`Erreur lors du chargement des ${titre.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }, [type, titre]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Valeurs propres à la catégorie sélectionnée
  const ownItems = useMemo(
    () =>
      items
        .filter((it) => it.categorie?.categorieId === categorieId)
        .sort((a, b) =>
          (a.libelleParametre || "").localeCompare(b.libelleParametre || "")
        ),
    [items, categorieId]
  );

  // Valeurs globales (héritées par toutes les catégories)
  const globalItems = useMemo(
    () =>
      items
        .filter((it) => !it.categorie)
        .sort((a, b) =>
          (a.libelleParametre || "").localeCompare(b.libelleParametre || "")
        ),
    [items]
  );

  const isDuplicate = (label, excludeId = null) =>
    ownItems.some(
      (it) =>
        it.parametreId !== excludeId &&
        (it.libelleParametre || "").trim().toLowerCase() ===
          label.trim().toLowerCase()
    ) ||
    globalItems.some(
      (it) =>
        (it.libelleParametre || "").trim().toLowerCase() ===
        label.trim().toLowerCase()
    );

  const handleAdd = async () => {
    const label = newValue.trim().toUpperCase();
    if (!label) return;
    if (!categorieId) {
      message.warning("Sélectionnez d'abord une catégorie.");
      return;
    }
    if (isDuplicate(label)) {
      message.warning("Cette valeur existe déjà pour cette catégorie.");
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`/api/parametre`, {
        libelleParametre: label,
        valeur: label,
        typeParametre: type,
        categorie,
      });
      setItems((prev) => [res.data, ...prev]);
      setNewValue("");
      message.success("Valeur ajoutée.");
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de l'ajout.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.parametreId);
    setEditingValue(item.libelleParametre || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleUpdate = async (item) => {
    const label = editingValue.trim().toUpperCase();
    if (!label) return;
    if (label === (item.libelleParametre || "").toUpperCase()) {
      cancelEdit();
      return;
    }
    if (isDuplicate(label, item.parametreId)) {
      message.warning("Cette valeur existe déjà pour cette catégorie.");
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/api/parametre`, {
        ...item,
        libelleParametre: label,
        valeur: label,
      });
      setItems((prev) =>
        prev.map((it) => (it.parametreId === item.parametreId ? res.data : it))
      );
      cancelEdit();
      message.success("Valeur modifiée.");
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la modification.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: `Supprimer « ${item.libelleParametre} » ?`,
      content: "Cette action est irréversible.",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await axiosInstance.delete(`/api/parametre/${item.parametreId}`);
          setItems((prev) =>
            prev.filter((it) => it.parametreId !== item.parametreId)
          );
          message.success("Valeur supprimée.");
        } catch (err) {
          console.error(err);
          message.error("Erreur lors de la suppression.");
        }
      },
    });
  };

  return (
    <Card
      title={
        <Space>
          <Text strong>{titre}</Text>
          <Tag color="blue">{ownItems.length}</Tag>
        </Space>
      }
      styles={{ body: { paddingTop: 12 } }}
    >
      {/* Ligne d'ajout */}
      <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
        <Input
          placeholder={
            categorieId
              ? `Nouvelle valeur (${titre.toLowerCase()})`
              : "Sélectionnez une catégorie"
          }
          value={newValue}
          disabled={!categorieId || saving}
          onChange={(e) => setNewValue(e.target.value)}
          onPressEnter={handleAdd}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={saving}
          disabled={!categorieId || !newValue.trim()}
          onClick={handleAdd}
        >
          Ajouter
        </Button>
      </Space.Compact>

      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : !categorieId ? (
        <Empty description="Sélectionnez une catégorie" />
      ) : ownItems.length === 0 ? (
        <Empty description={`Aucune valeur propre à cette catégorie`} />
      ) : (
        <List
          size="small"
          dataSource={ownItems}
          renderItem={(item) => {
            const isEditing = editingId === item.parametreId;
            return (
              <List.Item
                actions={
                  isEditing
                    ? [
                        <Tooltip title="Enregistrer" key="save">
                          <Button
                            type="text"
                            size="small"
                            icon={<CheckOutlined style={{ color: "#52c41a" }} />}
                            loading={saving}
                            onClick={() => handleUpdate(item)}
                          />
                        </Tooltip>,
                        <Tooltip title="Annuler" key="cancel">
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={cancelEdit}
                          />
                        </Tooltip>,
                      ]
                    : [
                        <Tooltip title="Modifier" key="edit">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => startEdit(item)}
                          />
                        </Tooltip>,
                        <Tooltip title="Supprimer" key="delete">
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(item)}
                          />
                        </Tooltip>,
                      ]
                }
              >
                {isEditing ? (
                  <Input
                    autoFocus
                    size="small"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onPressEnter={() => handleUpdate(item)}
                    style={{ maxWidth: 240 }}
                  />
                ) : (
                  <Text>{item.libelleParametre}</Text>
                )}
              </List.Item>
            );
          }}
        />
      )}

      {/* Valeurs globales héritées (lecture seule) */}
      {globalItems.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Valeurs globales héritées (toutes catégories) :
          </Text>
          <div style={{ marginTop: 6 }}>
            {globalItems.map((it) => (
              <Tag key={it.parametreId} style={{ marginBottom: 4 }}>
                {it.libelleParametre}
              </Tag>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

const CarrosseriesGenres = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategorieId, setSelectedCategorieId] = useState(null);
  const [loadingCats, setLoadingCats] = useState(false);

  useEffect(() => {
    setLoadingCats(true);
    axiosInstance
      .get(`/api/categorie`)
      .then((res) => {
        const cats = res.data || [];
        setCategories(cats);
        if (cats.length) setSelectedCategorieId(cats[0].categorieId);
      })
      .catch((err) => {
        console.error(err);
        message.error("Erreur lors du chargement des catégories.");
      })
      .finally(() => setLoadingCats(false));
  }, []);

  const selectedCategorie = useMemo(
    () => categories.find((c) => c.categorieId === selectedCategorieId) || null,
    [categories, selectedCategorieId]
  );

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="TARIFICATION" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} md={16}>
                    <Title level={5} style={{ margin: 0 }}>
                      Carrosseries &amp; Genres par catégorie
                    </Title>
                    <Text type="secondary">
                      Définissez, pour chaque catégorie d'engin, les carrosseries
                      et genres proposés dans les formulaires de contrat.
                    </Text>
                  </Col>
                  <Col xs={24} md={8}>
                    <Select
                      showSearch
                      style={{ width: "100%" }}
                      placeholder="Sélectionner une catégorie"
                      loading={loadingCats}
                      value={selectedCategorieId}
                      onChange={setSelectedCategorieId}
                      optionFilterProp="label"
                      options={categories.map((c) => ({
                        label: `${c.nom}${c.codeCategorie ? ` (${c.codeCategorie})` : ""}`,
                        value: c.categorieId,
                      }))}
                    />
                  </Col>
                </Row>
              </Card>

              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <ParamPanel
                    type="CAROSSERIE"
                    titre="Carrosseries"
                    categorie={selectedCategorie}
                  />
                </Col>
                <Col xs={24} lg={12}>
                  <ParamPanel
                    type="GENRE"
                    titre="Genres"
                    categorie={selectedCategorie}
                  />
                </Col>
              </Row>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarrosseriesGenres;
